/**
 * "Look into the code" for a FogBugz EVTX summary (epic PI-SPEC-SW-E307,
 * feature PI-SPEC-SW-1602, contract fogbugz 1).
 *
 * After the person reviews an EVTX summary in SpecTree Toolbox, they may ask
 * their own Claude Code to look into the product code behind it. On demand
 * only, app only (never posted to the case), for the Toolbox project and the
 * branch or tag the person chose. Three bridge operations:
 *
 * 1. `listCodeRefs` ({@link fogbugzCodeRefsInputSchema} ->
 *    {@link fogbugzCodeRefsResultSchema}): the branches and tags the person
 *    may pick, with `main` as the default.
 * 2. `investigateCode` ({@link fogbugzCodeInvestigateInputSchema} ->
 *    {@link fogbugzCodeInvestigateResultSchema}): two Claude passes with the
 *    engine's git work between them, because Claude never runs git:
 *    - pass 1, `locate`: the `toolbox-fogbugz-code` skill reads the digest
 *      and the reviewed sections, searches the repository copies and prints
 *      `SPECTREE_FOGBUGZ_CODE_LOCATE {json}`
 *      ({@link fogbugzCodeLocateMarkerSchema}): where the fault seems to be
 *      and who calls it, or `notFound` with a plain reason;
 *    - the engine writes `history.json` ({@link fogbugzCodeHistoryFileSchema}):
 *      the git log at the chosen ref of every located path;
 *    - pass 2, `report` (the same Claude session, resumed): the skill writes
 *      the three-layer report for the person's persona and prints
 *      `SPECTREE_FOGBUGZ_CODE_REPORT {json}`
 *      ({@link fogbugzCodeReportMarkerSchema}).
 * 3. `askCode` ({@link fogbugzCodeAskInputSchema} ->
 *    {@link fogbugzCodeAskResultSchema}): a follow-up question in the same
 *    thread (at most `FOGBUGZ_CODE_MAX_FOLLOW_UPS`), answered from what
 *    Claude already read; the skill prints `SPECTREE_FOGBUGZ_CODE_ANSWER
 *    {json}` ({@link fogbugzCodeAnswerMarkerSchema}).
 *
 * The toolset's existing `cancel` stops a code run too. Progress arrives as
 * `code-progress` events ({@link fogbugzCodeProgressEventSchema}). Every
 * marker is untrusted model output: it is parsed with these schemas, capped,
 * and the report is checked with {@link hasAllCodeReportSections} before the
 * page shows it. No server route and no database: the SpecTree API is not
 * involved.
 */
import { z } from "zod";
import { absoluteFilePathSchema } from "../formulator/api.js";
import { GROOMING_ACTIVITY_MAX_CHARS, MAX_GROOMING_REPOS } from "../grooming/constants.js";
import { TOOLBOX_PERSONAS } from "../roles/types.js";
import { isoDateTime } from "../zod-helpers.js";
import { FOGBUGZ_CODE_ANSWER_MARKER, FOGBUGZ_CODE_ANSWER_MAX_CHARS, FOGBUGZ_CODE_HISTORY_MAX_COMMITS, FOGBUGZ_CODE_LOCATE_MARKER, FOGBUGZ_CODE_LOCATIONS_MAX, FOGBUGZ_CODE_NOT_FOUND_REASON_MAX_CHARS, FOGBUGZ_CODE_QUESTION_MAX_CHARS, FOGBUGZ_CODE_REF_NAME_MAX, FOGBUGZ_CODE_REFS_MAX, FOGBUGZ_CODE_REPORT_MARKER, FOGBUGZ_CODE_REPORT_MAX_CHARS, FOGBUGZ_CODE_REPORT_SECTIONS, FOGBUGZ_CODE_RUN_PHASES, FOGBUGZ_CODE_TURN_INPUT_SCHEMA_VERSION, } from "./constants.js";
import { FOGBUGZ_ENGINE_ERROR_CODES, fogbugzCaseIdSchema, fogbugzEvtxDigestMarkdownSchema, fogbugzEvtxSectionsSchema, hasExactTopLevelHeadings, } from "./schemas.js";
// ---------------------------------------------------------------------------
// Error codes
// ---------------------------------------------------------------------------
/**
 * Every code a "Look into the code" operation resolves with on failure: the
 * toolset's {@link FOGBUGZ_ENGINE_ERROR_CODES} plus four of its own. For a
 * code run, `NO_SUMMARY_RETURNED` means Claude finished without a valid
 * locate, report or answer marker line, and `BUSY` that the app is already
 * running a FogBugz Claude run (a summary or a code run: one at a time).
 * `FOGBUGZ_ENGINE_ERROR_CODES` itself is unchanged, so `summarizeEvtx`
 * resolves with exactly the codes it did.
 */
export const FOGBUGZ_CODE_ERROR_CODES = [
    ...FOGBUGZ_ENGINE_ERROR_CODES,
    /**
     * The project's code is not set up on this computer: the project has no
     * mapped repositories, or no usable local copy of them. The page greys the
     * button out and links to Setup.
     */
    "CODE_NOT_SET_UP",
    /** The chosen branch or tag is in none of the project's repository copies. */
    "REF_NOT_FOUND",
    /**
     * The thread is gone: idle longer than `FOGBUGZ_CODE_THREAD_IDLE_MS`,
     * replaced by a newer `investigateCode`, or the app restarted. Look into
     * the code again to start a new one.
     */
    "THREAD_EXPIRED",
    /** The thread already took `FOGBUGZ_CODE_MAX_FOLLOW_UPS` follow-up questions. */
    "TOO_MANY_FOLLOW_UPS",
];
/** True when `value` is a "Look into the code" error code. */
export function isFogbugzCodeErrorCode(value) {
    return typeof value === "string" && FOGBUGZ_CODE_ERROR_CODES.includes(value);
}
/** A failed code operation: the code and the engine's plain sentence. */
export const fogbugzCodeFailureSchema = z.object({
    ok: z.literal(false),
    errorCode: z.enum(FOGBUGZ_CODE_ERROR_CODES),
    message: z.string(),
});
// ---------------------------------------------------------------------------
// Refs
// ---------------------------------------------------------------------------
/** The two kinds of ref the person may study: a branch or a tag (a release). */
export const FOGBUGZ_CODE_REF_KINDS = ["branch", "tag"];
/**
 * True when `name` is a branch or tag name the engine may hand to git: the
 * rules of `git check-ref-format --branch`, plus no leading `-` (so it can
 * never read as an option) and no backslash. Rejects blanks, control
 * characters, `~ ^ : ? * [ \`, `..`, `//`, `@{`, a lone `@`, a leading or
 * trailing `/`, a trailing `.` and a `.lock` ending or component start `.`.
 */
export function isFogbugzCodeRefName(name) {
    if (name.length === 0 || name.length > FOGBUGZ_CODE_REF_NAME_MAX)
        return false;
    if (name === "@" || name.startsWith("-") || name.startsWith("/") || name.endsWith("/") || name.endsWith(".")) {
        return false;
    }
    // eslint-disable-next-line no-control-regex
    if (/[\x00-\x20\x7f~^:?*[\\]/.test(name))
        return false;
    if (name.includes("..") || name.includes("//") || name.includes("@{"))
        return false;
    return name.split("/").every((part) => !part.startsWith(".") && !part.endsWith(".lock"));
}
/** A branch or tag of the project's repositories. */
export const fogbugzCodeRefSchema = z
    .object({
    kind: z.enum(FOGBUGZ_CODE_REF_KINDS),
    name: z.string().refine(isFogbugzCodeRefName, { message: "not a valid branch or tag name" }),
})
    .strict();
/** The ref studied when the person picks none: the `main` branch. */
export const FOGBUGZ_CODE_DEFAULT_REF = Object.freeze({ kind: "branch", name: "main" });
/** The Toolbox project (ADO project) whose mapped repositories are studied. */
export const fogbugzCodeProjectSchema = z.string().trim().min(1).max(256);
/** The input of `fogbugz.listCodeRefs`. */
export const fogbugzCodeRefsInputSchema = z.object({ project: fogbugzCodeProjectSchema }).strict();
/**
 * What `fogbugz.listCodeRefs` resolves with: the branches and tags the
 * project's repository copies all have (branches first, then tags, newest
 * tag first), at most `FOGBUGZ_CODE_REFS_MAX`, and `defaultRef`:
 * {@link FOGBUGZ_CODE_DEFAULT_REF} when the copies have `main`, else the
 * branch the copies are mapped to. `CODE_NOT_SET_UP` when the project has no
 * usable copy.
 */
export const fogbugzCodeRefsResultSchema = z.discriminatedUnion("ok", [
    z.object({
        ok: z.literal(true),
        refs: z.array(fogbugzCodeRefSchema).max(FOGBUGZ_CODE_REFS_MAX),
        defaultRef: fogbugzCodeRefSchema,
    }),
    fogbugzCodeFailureSchema,
]);
// ---------------------------------------------------------------------------
// Investigate and ask
// ---------------------------------------------------------------------------
/**
 * Who the report is written for: one of the Toolbox personas (`developer`,
 * `product-owner`, `qa-tester`). Project Managers and Admins carry the
 * persona their roles give them (`ROLE_PERSONA`: `product-owner`) unless they
 * chose another one on the project. `developer` gets files, functions, line
 * references and commits; the others get plain words with no code or paths.
 */
export const fogbugzCodePersonaSchema = z.enum(TOOLBOX_PERSONAS);
/** A thread id: one "Look into the code" conversation, made by the engine. */
export const fogbugzCodeThreadIdSchema = z.guid("threadId must be a UUID");
/** The report markdown: the three level-2 sections, capped. */
export const fogbugzCodeReportSchema = z.string().min(1).max(FOGBUGZ_CODE_REPORT_MAX_CHARS);
/** A follow-up answer, capped. */
export const fogbugzCodeAnswerSchema = z.string().trim().min(1).max(FOGBUGZ_CODE_ANSWER_MAX_CHARS);
/** A follow-up question, capped. */
export const fogbugzCodeQuestionSchema = z.string().trim().min(1).max(FOGBUGZ_CODE_QUESTION_MAX_CHARS);
/**
 * The input of `fogbugz.investigateCode`: which case and attachment, the
 * Toolbox project and ref to study, the persona to write for, the digest and
 * the reviewed summary sections.
 */
export const fogbugzCodeInvestigateInputSchema = z
    .object({
    caseId: fogbugzCaseIdSchema,
    fileName: z.string().min(1),
    project: fogbugzCodeProjectSchema,
    ref: fogbugzCodeRefSchema,
    persona: fogbugzCodePersonaSchema,
    digestMarkdown: fogbugzEvtxDigestMarkdownSchema,
    sections: fogbugzEvtxSectionsSchema,
})
    .strict();
/** What `fogbugz.investigateCode` resolves with: the new thread and its report, or why there is none. */
export const fogbugzCodeInvestigateResultSchema = z.discriminatedUnion("ok", [
    z.object({ ok: z.literal(true), threadId: fogbugzCodeThreadIdSchema, report: fogbugzCodeReportSchema }),
    fogbugzCodeFailureSchema,
]);
/** The input of `fogbugz.askCode`: a follow-up question in an open thread. */
export const fogbugzCodeAskInputSchema = z
    .object({
    threadId: fogbugzCodeThreadIdSchema,
    question: fogbugzCodeQuestionSchema,
})
    .strict();
/** What `fogbugz.askCode` resolves with: the answer, or why there is none. */
export const fogbugzCodeAskResultSchema = z.discriminatedUnion("ok", [
    z.object({ ok: z.literal(true), answer: fogbugzCodeAnswerSchema }),
    fogbugzCodeFailureSchema,
]);
// ---------------------------------------------------------------------------
// Progress events
// ---------------------------------------------------------------------------
/** The two code operations that run Claude and report progress. */
export const FOGBUGZ_CODE_OPERATIONS = ["investigate", "ask"];
/**
 * A code run moved to a new phase (`FOGBUGZ_CODE_RUN_PHASES`: finding the
 * code, reading history, writing the report, answering), or reports a new
 * plain activity line within it. One line replaces the previous one; pages
 * show `FOGBUGZ_CODE_RUN_PHASE_LABELS`. `threadId` is absent until the engine
 * made the thread. Pages parse with `safeParse` and ignore events that do
 * not parse.
 */
export const fogbugzCodeProgressEventSchema = z.object({
    type: z.literal("code-progress"),
    operation: z.enum(FOGBUGZ_CODE_OPERATIONS),
    threadId: fogbugzCodeThreadIdSchema.optional(),
    phase: z.enum(FOGBUGZ_CODE_RUN_PHASES),
    /** One plain phrase, for example "Reading the change history". Never a raw tool name, path or prompt. */
    activity: z.string().max(GROOMING_ACTIVITY_MAX_CHARS).optional(),
    /** When the run started, ISO 8601. */
    startedAt: z.string(),
});
// ---------------------------------------------------------------------------
// turn.json and history.json (engine -> skill)
// ---------------------------------------------------------------------------
/**
 * True when `value` is a path relative to a repository root: not empty, not
 * absolute (`/x`, `C:\x`, `\\server`), no `..` or `.` segment, no NUL. Either
 * separator is accepted (Grep reports `\` on Windows); the engine resolves it
 * inside the repository copy and refuses anything that leaves it.
 */
export function isRepoRelativePath(value) {
    if (value.length === 0 || value.includes("\0"))
        return false;
    if (/^[\\/]/.test(value) || /^[A-Za-z]:/.test(value))
        return false;
    return value.split(/[\\/]/).every((part) => part !== "" && part !== ".." && part !== ".");
}
/** A path inside a repository, relative to its root. */
export const fogbugzCodeRepoPathSchema = z
    .string()
    .max(1024)
    .refine(isRepoRelativePath, { message: "path must be relative to the repository root" });
/** A repository name as the project maps it. */
export const fogbugzCodeRepoNameSchema = z.string().trim().min(1).max(256);
/** One repository copy the skill may read: its mapped name and its absolute folder. */
export const fogbugzCodeTurnRepoSchema = z
    .object({
    name: fogbugzCodeRepoNameSchema,
    path: absoluteFilePathSchema,
})
    .strict();
/** A file in the turn folder: an absolute path, or a name relative to the turn file's folder. */
const turnFolderFileSchema = z.string().min(1).max(1024);
const codeTurnBaseShape = {
    schema: z.literal(FOGBUGZ_CODE_TURN_INPUT_SCHEMA_VERSION),
    threadId: fogbugzCodeThreadIdSchema,
    caseId: fogbugzCaseIdSchema,
    fileName: z.string().min(1),
    project: fogbugzCodeProjectSchema,
    ref: fogbugzCodeRefSchema,
    persona: fogbugzCodePersonaSchema,
    /** The digest markdown file. */
    digestFile: turnFolderFileSchema,
    /** The reviewed summary sections file. */
    sectionsFile: turnFolderFileSchema,
    /** The repository copies, checked out at `ref`. The skill reads nothing outside them and the turn folder. */
    repos: z.array(fogbugzCodeTurnRepoSchema).min(1).max(MAX_GROOMING_REPOS),
};
/**
 * `turn.json`: what the engine hands the `toolbox-fogbugz-code` skill for one
 * Claude run, written (mode 0600) in the thread's folder; person text goes
 * here, never on the command line. By `mode`:
 * - `locate`: pass 1, find the fault and its callers;
 * - `report`: pass 2 (the session resumed), with `historyFile`, the
 *   `history.json` the engine wrote after pass 1;
 * - `ask`: a follow-up (the session resumed), with `question`.
 */
export const fogbugzCodeTurnFileSchema = z.discriminatedUnion("mode", [
    z.object({ ...codeTurnBaseShape, mode: z.literal("locate") }).strict(),
    z.object({ ...codeTurnBaseShape, mode: z.literal("report"), historyFile: turnFolderFileSchema }).strict(),
    z.object({ ...codeTurnBaseShape, mode: z.literal("ask"), question: fogbugzCodeQuestionSchema }).strict(),
]);
/** A full or abbreviated git commit id (7-64 lowercase hex characters). */
export const fogbugzCodeCommitShaSchema = z.string().regex(/^[0-9a-f]{7,64}$/);
/** One commit of a path's history, as the engine read it with git. Author name only, never an email. */
export const fogbugzCodeHistoryCommitSchema = z
    .object({
    sha: fogbugzCodeCommitShaSchema,
    /** Commit (committer) date, ISO 8601 with offset. */
    date: isoDateTime({ offset: true }),
    author: z.string().max(200),
    subject: z.string().max(500),
})
    .strict();
/**
 * The code location roles: `fault` (where the error seems to come from) and
 * `caller` (code that calls into it).
 */
export const FOGBUGZ_CODE_LOCATION_ROLES = ["fault", "caller"];
/**
 * `history.json`: the git history the engine read between the locate and
 * report passes, one entry per located path at `ref` (`refCommit`), newest
 * commit first, at most `FOGBUGZ_CODE_HISTORY_MAX_COMMITS` each (`truncated`
 * when there were more). `missing` marks a located path that does not exist
 * at `ref` (it then has no commits). `generatedAt` is "now" for judging
 * whether a change is recent (`FOGBUGZ_CODE_RECENT_DAYS`).
 */
export const fogbugzCodeHistoryFileSchema = z
    .object({
    schema: z.literal(FOGBUGZ_CODE_TURN_INPUT_SCHEMA_VERSION),
    ref: fogbugzCodeRefSchema,
    generatedAt: isoDateTime({ offset: true }),
    paths: z
        .array(z
        .object({
        repo: fogbugzCodeRepoNameSchema,
        refCommit: fogbugzCodeCommitShaSchema,
        path: fogbugzCodeRepoPathSchema,
        role: z.enum(FOGBUGZ_CODE_LOCATION_ROLES),
        missing: z.boolean().optional(),
        truncated: z.boolean(),
        commits: z.array(fogbugzCodeHistoryCommitSchema).max(FOGBUGZ_CODE_HISTORY_MAX_COMMITS),
    })
        .strict())
        .max(FOGBUGZ_CODE_LOCATIONS_MAX),
})
    .strict();
// ---------------------------------------------------------------------------
// Marker lines (skill -> engine)
// ---------------------------------------------------------------------------
/**
 * One code location the locate pass found. `repo` is one of the turn's repo
 * names (the engine drops a location naming another), `path` is relative to
 * that repository's root, `symbol` the function, method, class or procedure,
 * `lineHint` an approximate line number.
 */
export const fogbugzCodeLocationSchema = z
    .object({
    repo: fogbugzCodeRepoNameSchema,
    path: fogbugzCodeRepoPathSchema,
    symbol: z.string().trim().min(1).max(300).optional(),
    lineHint: z.number().int().positive().optional(),
    role: z.enum(FOGBUGZ_CODE_LOCATION_ROLES),
})
    .strict();
/**
 * The JSON after `SPECTREE_FOGBUGZ_CODE_LOCATE` (pass 1): either `locations`
 * (at least one `fault`, at most `FOGBUGZ_CODE_LOCATIONS_MAX` in all) or
 * `notFound`, a plain reason the code could not be found. With `notFound`
 * the engine writes a `history.json` with no paths and still runs the report
 * pass, which says what was searched.
 */
export const fogbugzCodeLocateMarkerSchema = z.union([
    z
        .object({
        threadId: fogbugzCodeThreadIdSchema,
        locations: z
            .array(fogbugzCodeLocationSchema)
            .min(1)
            .max(FOGBUGZ_CODE_LOCATIONS_MAX)
            .refine((locations) => locations.some((location) => location.role === "fault"), {
            message: "at least one location must be the fault",
        }),
    })
        .strict(),
    z
        .object({
        threadId: fogbugzCodeThreadIdSchema,
        notFound: z.string().trim().min(1).max(FOGBUGZ_CODE_NOT_FOUND_REASON_MAX_CHARS),
    })
        .strict(),
]);
/**
 * True when `report` carries the three {@link FOGBUGZ_CODE_REPORT_SECTIONS}
 * headings (`## Where it failed`, `## Code review`, `## Change history`), in
 * order, each once, and no other level-1 or level-2 heading outside fenced
 * code blocks. The same CommonMark-aware reader as `hasAllEvtxSections`.
 */
export function hasAllCodeReportSections(report) {
    return hasExactTopLevelHeadings(report, FOGBUGZ_CODE_REPORT_SECTIONS);
}
/** The JSON after `SPECTREE_FOGBUGZ_CODE_REPORT` (pass 2): the report, with exactly the three headings. */
export const fogbugzCodeReportMarkerSchema = z
    .object({
    threadId: fogbugzCodeThreadIdSchema,
    report: fogbugzCodeReportSchema.refine(hasAllCodeReportSections, {
        message: "report must carry exactly the three report headings, in order",
    }),
})
    .strict();
/** The JSON after `SPECTREE_FOGBUGZ_CODE_ANSWER` (a follow-up): the answer. */
export const fogbugzCodeAnswerMarkerSchema = z
    .object({
    threadId: fogbugzCodeThreadIdSchema,
    answer: fogbugzCodeAnswerSchema,
})
    .strict();
/**
 * The last `<marker> {json}` line in `text`, parsed with `schema`; null when
 * there is none or the last one does not parse.
 */
function parseMarkerLine(text, marker, schema) {
    const lines = text.split(/\r?\n/);
    for (let i = lines.length - 1; i >= 0; i -= 1) {
        const line = (lines[i] ?? "").trim();
        if (!line.startsWith(`${marker} `) && line !== marker)
            continue;
        let json;
        try {
            json = JSON.parse(line.slice(marker.length).trim());
        }
        catch {
            return null;
        }
        const parsed = schema.safeParse(json);
        return parsed.success ? parsed.data : null;
    }
    return null;
}
/** The last valid `SPECTREE_FOGBUGZ_CODE_LOCATE` line in Claude's output, or null. */
export function parseFogbugzCodeLocateLine(text) {
    return parseMarkerLine(text, FOGBUGZ_CODE_LOCATE_MARKER, fogbugzCodeLocateMarkerSchema);
}
/** The last valid `SPECTREE_FOGBUGZ_CODE_REPORT` line in Claude's output, or null. */
export function parseFogbugzCodeReportLine(text) {
    return parseMarkerLine(text, FOGBUGZ_CODE_REPORT_MARKER, fogbugzCodeReportMarkerSchema);
}
/** The last valid `SPECTREE_FOGBUGZ_CODE_ANSWER` line in Claude's output, or null. */
export function parseFogbugzCodeAnswerLine(text) {
    return parseMarkerLine(text, FOGBUGZ_CODE_ANSWER_MARKER, fogbugzCodeAnswerMarkerSchema);
}
//# sourceMappingURL=code.js.map